import { Row } from "@hightouch/htui";
import {
  useHasProductTabs,
  useProjectsAccess,
} from "src/components/layout/nav/sections/hooks/projects";
import { InboxButton } from "src/pages/projects/inbox/inbox-button";
import { WorkspaceSelector } from "src/components/layout/nav/workspace-selector";
import { WorkspaceLogo } from "src/components/layout/nav/initial-tile";
import { NavSearchButton } from "src/components/layout/nav/mini-nav/mini-nav-context";
import { useNavWidth } from "src/contexts/nav-width-context";
import { FullLogo } from "./logos";
import { NavToggleButton } from "./toggle-button";
import { useUser } from "src/contexts/user-context";

/**
 * Navigation header displays the logo, the inbox for viewers of the projects area, and a button to toggle the sidebar.
 */
export function NavHeader({
  showToggleHoverState = false,
}: {
  showToggleHoverState?: boolean;
}) {
  const { isNavOpen } = useNavWidth();
  const { user } = useUser();
  // The classic nav has no Inbox entry, so AMP users who switched back and Ad Studio users reach the inbox here.
  const { isEnabled: hasInbox } = useProjectsAccess();

  const orgLogo = user?.organization?.logo_url;

  return (
    <Row
      as="header"
      aria-label="Navigation header"
      align="center"
      gap={2}
      h={orgLogo ? 10 : 8}
      justify={isNavOpen ? "space-between" : "center"}
      pl={orgLogo && isNavOpen ? 0 : 2}
      px={2}
      pr={isNavOpen ? 0 : 2} // Only have left padding on expanded state, since toggle button has it's own padding for hover state.
      py={1}
    >
      {isNavOpen && <FullLogo />}
      <Row align="center" gap={1} flexShrink={0}>
        {isNavOpen && hasInbox && <InboxButton />}
        <NavToggleButton showHoverState={showToggleHoverState} />
      </Row>
    </Row>
  );
}

/** The AMP nav's header: the workspace's logo and name open the workspace switcher, then the inbox and search at the end. */
export function AmpNavHeader() {
  // The product nav has no Inbox entry, so the inbox sits here, beside the switcher.
  const hasInbox = useHasProductTabs();
  return (
    <Row
      as="header"
      aria-label="Navigation header"
      align="center"
      gap={2}
      position="relative"
    >
      {/* First, so it is the nav's first stop for the keyboard, though it shows only on focus, over the switcher's end. */}
      <NavToggleButton />
      <Row flex={1} minWidth={0}>
        <WorkspaceSelector
          appearance="header"
          leading={<WorkspaceLogo size="xs" />}
        />
      </Row>
      {/* On a phone the toggles' 44px hit areas need the wider gap to meet without overlapping, as in the crumb bar's end slot. */}
      <Row align="center" gap={{ base: 5, md: 2 }} flexShrink={0}>
        {hasInbox && <InboxButton />}
        <NavSearchButton />
      </Row>
    </Row>
  );
}
